import siteRaw from './site.json';
import homepageRaw from './homepage.json';
import productsRaw from './products.json';
import offersRaw from './offers.json';
import galleryRaw from './gallery.json';
import testimonialsRaw from './testimonials.json';
import faqRaw from './faq.json';
import {
  normalizeFaq,
  normalizeGallery,
  normalizeHomepage,
  normalizeOffers,
  normalizeProducts,
  normalizeSite,
  normalizeTestimonials
} from './normalize';

export { activeOfferFor } from './normalize';

export const contentKeys = ['site', 'homepage', 'products', 'offers', 'gallery', 'testimonials', 'faq'];
export const contentDefaults = {
  site: siteRaw,
  homepage: homepageRaw,
  products: productsRaw,
  offers: offersRaw,
  gallery: galleryRaw,
  testimonials: testimonialsRaw,
  faq: faqRaw
};

export const siteContent = normalizeSite(siteRaw);
export const homepageContent = normalizeHomepage(homepageRaw);
export const productsContent = normalizeProducts(productsRaw, siteContent.ordering.defaultLeadTime);
export const offersContent = normalizeOffers(offersRaw);
export const galleryContent = normalizeGallery(galleryRaw);
export const testimonialsContent = normalizeTestimonials(testimonialsRaw);
export const faqContent = normalizeFaq(faqRaw);

const clone = (value) => JSON.parse(JSON.stringify(value));
let storedContent = clone(contentDefaults);

const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

// A few older storefront modules hold a nested content object or list by
// reference. Sync recursively so a remote CMS refresh updates those values
// without forcing a brittle page reload or leaving a mixed old/new screen.
const syncValue = (current, next) => {
  if (Array.isArray(current) && Array.isArray(next)) return replaceArray(current, next);
  if (isRecord(current) && isRecord(next)) return replaceObject(current, next);
  return next;
};

const replaceObject = (target, source) => {
  Object.keys(target).forEach((key) => {
    if (!(key in source)) delete target[key];
  });
  Object.entries(source).forEach(([key, value]) => {
    target[key] = syncValue(target[key], value);
  });
  return target;
};

const replaceArray = (target, source) => {
  source.forEach((value, index) => {
    target[index] = syncValue(target[index], value);
  });
  target.splice(source.length);
  return target;
};

// Editing must preserve the database shape, including hidden variants and
// blank fields. Normalized storefront data is a separate, derived view.
export const getContentSnapshot = () => clone(storedContent);

// Existing storefront components import these stable objects directly. Updating
// them in place lets Supabase content go live without destabilising the UI.
export const applyRemoteContent = (remote = {}) => {
  storedContent = Object.fromEntries(contentKeys.map((key) => [key, clone(remote[key] || contentDefaults[key])]));
  const nextSite = normalizeSite(remote.site || contentDefaults.site);
  const nextHomepage = normalizeHomepage(remote.homepage || contentDefaults.homepage);
  const nextProducts = normalizeProducts(remote.products || contentDefaults.products, nextSite.ordering.defaultLeadTime);
  const nextOffers = normalizeOffers(remote.offers || contentDefaults.offers);
  const nextGallery = normalizeGallery(remote.gallery || contentDefaults.gallery);
  const nextTestimonials = normalizeTestimonials(remote.testimonials || contentDefaults.testimonials);
  const nextFaq = normalizeFaq(remote.faq || contentDefaults.faq);

  replaceObject(siteContent, nextSite);
  replaceObject(homepageContent, nextHomepage);
  replaceArray(productsContent, nextProducts);
  replaceArray(offersContent, nextOffers);
  replaceObject(galleryContent, nextGallery);
  replaceObject(testimonialsContent, nextTestimonials);
  replaceObject(faqContent, nextFaq);

  return getContentSnapshot();
};
